import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { publicApi } from '@/lib/api';
import { LEGAL_LINKS } from '@/lib/legal';
import { isIOS, isAndroid, IOS_APP_URL } from '@/lib/platform';
import { AppChoiceModal } from '@/components/marketing/AppChoiceModal';
import { InstallBanner } from '@/components/marketing/InstallBanner';
import { Button } from '@/components/ui/button';
import {
  Menu,
  X,
  QrCode,
  UtensilsCrossed,
  CalendarCheck,
  Leaf,
  RefreshCw,
  Clock,
  Sparkles,
  Smartphone,
  MapPin,
  Phone,
  Mail,
  Check,
  Apple,
  ChevronRight,
  Instagram,
  Facebook,
} from 'lucide-react';

const IMG = {
  hero: 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  spices: 'https://images.unsplash.com/photo-1581600140682-d4e68c8cde32?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
  pickup: 'https://images.unsplash.com/photo-1778792113398-358b0a771d8e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000',
  food1: 'https://images.unsplash.com/photo-1777613112862-f9ca6d6fa56e?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
  food2: 'https://images.unsplash.com/photo-1767114915989-c6ab3c8fc42e?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
  food3: 'https://images.unsplash.com/photo-1786222084052-fb6f27bfb135?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
};

// ── Editable business details (placeholders — swap for real values) ──
const BUSINESS = {
  city: 'Edmonton, AB',
  address: '9516 Ellerslie Road SW, Edmonton, AB T6X 0K6',
  phone: '(780) 909-0988',
  email: 'Thetiffinyeg@gmail.com',
  hours: 'Mon – Sat · 11:00 AM – 8:00 PM',
  instagram: '#',
  facebook: '#',
};

const NAV_LINKS = [
  { label: 'How it works', href: '#how' },
  { label: 'Plans', href: '#plans' },
  { label: 'The Food', href: '#food' },
  { label: 'App', href: '#app' },
  { label: 'Visit', href: '#visit' },
];

function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 bg-bone/85 backdrop-blur-md border-b border-black/5">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <a href="#top" data-testid="nav-brand" className="flex items-center shrink-0">
          <img src="/logo-wordmark.png" alt="The Tiffin by The Virsa" className="h-9 sm:h-10 object-contain" />
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              className="text-sm font-medium text-charcoal/80 hover:text-saffron transition-colors duration-150"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden sm:flex items-center gap-2.5">
          <Link to="/cxlogin">
            <Button
              data-testid="nav-login-btn"
              variant="ghost"
              className="h-10 rounded-full px-5 text-charcoal hover:bg-black/5 font-semibold"
            >
              Log In
            </Button>
          </Link>
          <Link to="/register">
            <Button
              data-testid="nav-register-btn"
              className="h-10 rounded-full px-6 bg-saffron hover:bg-saffron-hover text-white font-semibold transition-colors duration-150"
            >
              Register
            </Button>
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="sm:hidden inline-flex items-center justify-center h-10 w-10 rounded-full hover:bg-black/5 transition-colors duration-150"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="sm:hidden border-t border-black/5 bg-bone px-5 py-4 space-y-1" data-testid="mobile-menu">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="block py-2.5 text-base font-medium text-charcoal"
            >
              {l.label}
            </a>
          ))}
          <div className="flex gap-3 pt-3">
            <Link to="/cxlogin" className="flex-1">
              <Button data-testid="mobile-login-btn" variant="outline" className="w-full h-11 rounded-full font-semibold">
                Log In
              </Button>
            </Link>
            <Link to="/register" className="flex-1">
              <Button data-testid="mobile-register-btn" className="w-full h-11 rounded-full bg-saffron hover:bg-saffron-hover text-white font-semibold">
                Register
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Hero() {
  return (
    <section id="top" className="relative overflow-hidden bg-bone">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-14 pb-16 sm:pt-20 sm:pb-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div className="max-w-xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-coriander/10 text-coriander px-4 py-1.5 text-xs font-semibold tracking-wide uppercase">
            <Sparkles size={14} /> Now serving in {BUSINESS.city}
          </span>
          <h1 className="mt-6 font-heading font-900 tracking-tight text-charcoal text-5xl sm:text-6xl lg:text-7xl leading-[0.98]">
            Home-style tiffins,{' '}
            <span className="text-saffron">fresh every day.</span>
          </h1>
          <p className="mt-6 text-lg text-muted-ink leading-relaxed">
            Daily Fresh. Daily Different. Subscribe to a tiffin plan, get your personal QR code, and pick up
            wholesome Indian meals whenever you like — no cooking, no cleanup.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <Link to="/register">
              <Button
                data-testid="hero-register-btn"
                className="w-full sm:w-auto h-13 min-h-[52px] rounded-full px-8 bg-saffron hover:bg-saffron-hover text-white text-base font-semibold transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-lg"
              >
                Start your subscription <ChevronRight size={18} className="ml-1" />
              </Button>
            </Link>
            <Link to="/cxlogin">
              <Button
                data-testid="hero-login-btn"
                variant="outline"
                className="w-full sm:w-auto h-13 min-h-[52px] rounded-full px-8 border-charcoal/20 text-charcoal hover:bg-black/5 text-base font-semibold transition-colors duration-150"
              >
                I already have an account
              </Button>
            </Link>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-muted-ink">
            <span className="flex items-center gap-2"><Check size={16} className="text-coriander" /> Rotating daily menu</span>
            <span className="flex items-center gap-2"><Check size={16} className="text-coriander" /> Flexible pickup</span>
            <span className="flex items-center gap-2"><Check size={16} className="text-coriander" /> Cancel anytime</span>
          </div>
        </div>

        <div className="relative">
          <div className="relative rounded-[2rem] overflow-hidden shadow-2xl ring-1 ring-black/10 aspect-[4/5]">
            <img src={IMG.hero} alt="Fresh Indian tiffin meal" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/40 to-transparent" />
          </div>
          <div className="hidden sm:block absolute -bottom-6 -left-6 w-40 rounded-2xl overflow-hidden shadow-xl ring-1 ring-black/10 rotate-[-4deg]">
            <img src={IMG.spices} alt="Fresh spices" className="w-full h-28 object-cover" />
          </div>
          <div className="absolute -top-5 -right-2 sm:right-6 bg-white rounded-2xl shadow-xl ring-1 ring-black/5 px-5 py-3">
            <p className="font-heading font-700 text-2xl text-charcoal leading-none">100%</p>
            <p className="text-xs text-muted-ink mt-1">Freshly cooked</p>
          </div>
        </div>
      </div>
    </section>
  );
}

const HIGHLIGHTS = [
  { icon: Leaf, label: 'Freshly cooked daily' },
  { icon: RefreshCw, label: 'Menu that rotates' },
  { icon: QrCode, label: 'Tap-free QR pickup' },
  { icon: Clock, label: 'Ready when you are' },
];

function Highlights() {
  return (
    <section className="bg-obsidian">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-5">
        {HIGHLIGHTS.map((h) => (
          <div key={h.label} className="flex items-center gap-3 text-white/90">
            <h.icon size={20} className="text-saffron shrink-0" />
            <span className="text-sm font-medium">{h.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

const STEPS = [
  {
    icon: UtensilsCrossed,
    title: 'Choose your plan',
    body: 'Pick a tiffin plan that fits your appetite and schedule. Add extras and apply a discount code at checkout.',
  },
  {
    icon: QrCode,
    title: 'Get your QR code',
    body: 'Your account gets a personal QR pass. Keep it on your phone — it holds your remaining tiffin balance.',
  },
  {
    icon: CalendarCheck,
    title: 'Show & collect',
    body: 'Drop by, show your QR at the counter, and we deduct one tiffin. Fresh food in hand, zero hassle.',
  },
];

function HowItWorks() {
  return (
    <section id="how" className="bg-bone scroll-mt-16">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
        <div className="max-w-2xl">
          <p className="text-saffron font-semibold text-sm uppercase tracking-wide">How it works</p>
          <h2 className="mt-3 font-heading font-900 tracking-tight text-charcoal text-4xl sm:text-5xl">
            Three steps to fresh meals.
          </h2>
          <p className="mt-4 text-lg text-muted-ink">
            No daily ordering, no queues. Set it up once and simply show up.
          </p>
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <div
              key={s.title}
              className="relative bg-white rounded-3xl border border-black/5 shadow-sm p-8 transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-lg"
            >
              <span className="absolute top-8 right-8 font-heading font-900 text-5xl text-black/5 leading-none">
                {i + 1}
              </span>
              <div className="h-14 w-14 rounded-2xl bg-saffron/10 flex items-center justify-center">
                <s.icon size={26} className="text-saffron" />
              </div>
              <h3 className="mt-6 font-heading font-700 text-xl text-charcoal">{s.title}</h3>
              <p className="mt-2 text-muted-ink leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Plans() {
  const [plans, setPlans] = useState(null);
  const [appModalOpen, setAppModalOpen] = useState(false);
  const navigate = useNavigate();

  // iOS & Android visitors get the app-vs-browser choice; everyone else goes straight to registration.
  const handleChoose = () => {
    if (isIOS() || isAndroid()) setAppModalOpen(true);
    else navigate('/register');
  };

  useEffect(() => {
    let active = true;
    const fetchPlans = () =>
      publicApi
        .get('/registration/options')
        .then(({ data }) => { if (active) setPlans(data.plans || []); })
        // On a transient failure keep whatever is already shown (don't blank the section).
        .catch(() => { if (active) setPlans((prev) => prev ?? []); });

    fetchPlans();
    const interval = setInterval(fetchPlans, 30000);
    const onVisible = () => { if (document.visibilityState === 'visible') fetchPlans(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      active = false;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);

  const popularIdx = plans && plans.length >= 3 ? 1 : -1;

  return (
    <section id="plans" className="bg-obsidian text-white scroll-mt-16">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
        <div className="max-w-2xl">
          <p className="text-saffron font-semibold text-sm uppercase tracking-wide">Our plans</p>
          <h2 className="mt-3 font-heading font-900 tracking-tight text-4xl sm:text-5xl">
            Pick a plan, prepay, save.
          </h2>
          <p className="mt-4 text-lg text-white/70">
            Every plan is a bundle of tiffins with a validity window. Prepay once and redeem whenever you like.
          </p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="plans-grid">
          {plans === null &&
            [0, 1, 2].map((i) => (
              <div key={i} className="rounded-3xl bg-white/5 border border-white/10 p-8 h-72 animate-pulse" />
            ))}

          {plans && plans.length === 0 && (
            <div className="col-span-full rounded-3xl bg-white/5 border border-white/10 p-10 text-center">
              <p className="text-white/70">
                Our plans are being updated. Create an account to see the latest offers at checkout.
              </p>
              <Link to="/register">
                <Button data-testid="plans-empty-register-btn" className="mt-5 h-12 rounded-full px-7 bg-saffron hover:bg-saffron-hover text-white font-semibold">
                  Register now
                </Button>
              </Link>
            </div>
          )}

          {plans &&
            plans.map((p, i) => {
              const popular = i === popularIdx;
              return (
                <div
                  key={p.id}
                  data-testid={`plan-card-${p.id}`}
                  className={`relative rounded-3xl p-8 flex flex-col transition-[transform,box-shadow] duration-200 hover:-translate-y-1 ${
                    popular
                      ? 'bg-white text-charcoal shadow-2xl ring-2 ring-saffron'
                      : 'bg-white/5 border border-white/10 hover:bg-white/10'
                  }`}
                >
                  {popular && (
                    <span className="absolute -top-3 left-8 rounded-full bg-saffron px-4 py-1 text-xs font-bold text-white uppercase tracking-wide">
                      Most popular
                    </span>
                  )}
                  <h3 className={`font-heading font-700 text-2xl ${popular ? 'text-charcoal' : 'text-white'}`}>
                    {p.name}
                  </h3>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className="font-heading font-900 text-5xl">${Number(p.price).toFixed(0)}</span>
                    <span className={popular ? 'text-muted-ink' : 'text-white/60'}>
                      .{(Number(p.price) % 1).toFixed(2).slice(2)}
                    </span>
                  </div>
                  <p className={`mt-3 text-sm ${popular ? 'text-muted-ink' : 'text-white/70'}`}>
                    {p.tiffinCount} tiffins · valid {p.validityDays} days
                  </p>
                  {p.description && (
                    <p className={`mt-4 text-sm leading-relaxed ${popular ? 'text-muted-ink' : 'text-white/60'}`}>
                      {p.description}
                    </p>
                  )}
                  <div className="mt-auto pt-8">
                    <Button
                      data-testid={`plan-register-${p.id}`}
                      onClick={handleChoose}
                      className={`w-full h-12 rounded-full font-semibold transition-colors duration-150 ${
                        popular
                          ? 'bg-saffron hover:bg-saffron-hover text-white'
                          : 'bg-white text-charcoal hover:bg-white/90'
                      }`}
                    >
                      Choose {p.name}
                    </Button>
                  </div>
                </div>
              );
            })}
        </div>

        <p className="mt-8 text-sm text-white/50">
          Prices shown in CAD. Have a discount code? Apply it during registration.
        </p>
      </div>
      <AppChoiceModal open={appModalOpen} onOpenChange={setAppModalOpen} />
    </section>
  );
}

const FEATURES = [
  { icon: Leaf, title: 'Wholesome & fresh', body: 'Cooked in small batches every day with quality ingredients and real spices — never reheated leftovers.' },
  { icon: RefreshCw, title: 'A menu that changes', body: 'Daily Different means you look forward to every pickup. New combinations keep things interesting.' },
  { icon: QrCode, title: 'Effortless pickup', body: 'Your QR pass tracks your balance automatically. Show it, collect, and go.' },
  { icon: Sparkles, title: 'Extras & discounts', body: 'Add sides and treats to any plan, and stack discount codes to save even more.' },
];

function Food() {
  return (
    <section id="food" className="bg-bone scroll-mt-16">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div className="grid grid-cols-2 gap-4">
            <img src={IMG.food1} alt="Home-cooked curries" className="rounded-2xl object-cover w-full h-56 shadow-sm ring-1 ring-black/5 col-span-2" />
            <img src={IMG.food2} alt="Indian bowls with rice" className="rounded-2xl object-cover w-full h-44 shadow-sm ring-1 ring-black/5" />
            <img src={IMG.food3} alt="Traditional Indian plate" className="rounded-2xl object-cover w-full h-44 shadow-sm ring-1 ring-black/5" />
          </div>
          <div>
            <p className="text-saffron font-semibold text-sm uppercase tracking-wide">Why The Tiffin</p>
            <h2 className="mt-3 font-heading font-900 tracking-tight text-charcoal text-4xl sm:text-5xl">
              Real food, made the way home does it.
            </h2>
            <div className="mt-10 grid sm:grid-cols-2 gap-x-8 gap-y-8">
              {FEATURES.map((f) => (
                <div key={f.title}>
                  <div className="h-11 w-11 rounded-xl bg-coriander/10 flex items-center justify-center">
                    <f.icon size={22} className="text-coriander" />
                  </div>
                  <h3 className="mt-4 font-heading font-700 text-lg text-charcoal">{f.title}</h3>
                  <p className="mt-1.5 text-sm text-muted-ink leading-relaxed">{f.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AppPromo() {
  return (
    <section id="app" className="bg-coriander text-white scroll-mt-16">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <div className="max-w-xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide">
            <Smartphone size={14} /> Customer app
          </span>
          <h2 className="mt-6 font-heading font-900 tracking-tight text-4xl sm:text-5xl">
            Your tiffin, in your pocket.
          </h2>
          <p className="mt-4 text-lg text-white/80 leading-relaxed">
            Track your balance, view your pickup history, manage your subscription, and flash your QR at the
            counter — all from the mobile app for iOS and Android.
          </p>
          <ul className="mt-8 space-y-3">
            {['Live tiffin balance & QR pass', 'Full pickup history calendar', 'Renew or top-up in a tap'].map((t) => (
              <li key={t} className="flex items-center gap-3 text-white/90">
                <Check size={18} className="text-white shrink-0" /> {t}
              </li>
            ))}
          </ul>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <a
              href={IOS_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="appstore-download-btn"
            >
              <Button className="h-13 min-h-[52px] rounded-full px-7 bg-obsidian text-white hover:bg-obsidian/90 text-base font-bold gap-2 transition-colors duration-150">
                <Apple size={20} /> Download on the App Store
              </Button>
            </a>
            <Link to="/register">
              <Button
                data-testid="app-register-btn"
                variant="outline"
                className="h-13 min-h-[52px] rounded-full px-7 bg-white/10 border-white/50 text-white hover:bg-white/20 text-base font-bold transition-colors duration-150"
              >
                Create your account
              </Button>
            </Link>
          </div>
        </div>
        <div className="relative flex justify-center">
          <div className="relative w-64 rounded-[2.5rem] overflow-hidden shadow-2xl ring-4 ring-white/20 aspect-[9/16] bg-obsidian">
            <img src={IMG.hero} alt="The Tiffin app" className="w-full h-full object-cover opacity-90" />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 to-transparent" />
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white rounded-2xl px-5 py-4 shadow-xl text-center">
              <QrCode size={40} className="text-charcoal mx-auto" />
              <p className="mt-2 text-xs font-semibold text-charcoal">Show at pickup</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Visit() {
  return (
    <section id="visit" className="bg-bone scroll-mt-16">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28 grid lg:grid-cols-2 gap-12 items-stretch">
        <div>
          <p className="text-saffron font-semibold text-sm uppercase tracking-wide">Visit us</p>
          <h2 className="mt-3 font-heading font-900 tracking-tight text-charcoal text-4xl sm:text-5xl">
            Come say hello.
          </h2>
          <p className="mt-4 text-lg text-muted-ink">Pick up your tiffins fresh from our kitchen counter.</p>
          <div className="mt-10 space-y-6">
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-xl bg-saffron/10 flex items-center justify-center shrink-0">
                <MapPin size={20} className="text-saffron" />
              </div>
              <div>
                <p className="font-heading font-700 text-charcoal">Address</p>
                <p className="text-muted-ink">{BUSINESS.address}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-xl bg-saffron/10 flex items-center justify-center shrink-0">
                <Clock size={20} className="text-saffron" />
              </div>
              <div>
                <p className="font-heading font-700 text-charcoal">Hours</p>
                <p className="text-muted-ink">{BUSINESS.hours}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-xl bg-saffron/10 flex items-center justify-center shrink-0">
                <Phone size={20} className="text-saffron" />
              </div>
              <div>
                <p className="font-heading font-700 text-charcoal">Phone</p>
                <a href={`tel:${BUSINESS.phone}`} data-testid="visit-phone-link" className="text-muted-ink hover:text-saffron transition-colors duration-150">
                  {BUSINESS.phone}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-xl bg-saffron/10 flex items-center justify-center shrink-0">
                <Mail size={20} className="text-saffron" />
              </div>
              <div>
                <p className="font-heading font-700 text-charcoal">Email</p>
                <a href={`mailto:${BUSINESS.email}`} data-testid="visit-email-link" className="text-muted-ink hover:text-saffron transition-colors duration-150">
                  {BUSINESS.email}
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="relative rounded-[2rem] overflow-hidden shadow-xl ring-1 ring-black/10 min-h-[320px]">
          <img src={IMG.pickup} alt="The Tiffin pickup counter" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian/50 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6 text-white">
            <p className="font-heading font-700 text-xl">Fresh at the counter</p>
            <p className="text-white/80 text-sm">Show your QR, collect your tiffin, done.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function CtaBanner() {
  return (
    <section className="bg-saffron">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
        <div>
          <h2 className="font-heading font-900 tracking-tight text-white text-3xl sm:text-4xl">
            Ready to eat fresh every day?
          </h2>
          <p className="mt-2 text-white/90 text-lg">Join The Tiffin family in minutes.</p>
        </div>
        <div className="flex gap-3 shrink-0">
          <Link to="/register">
            <Button
              data-testid="cta-register-btn"
              className="h-13 min-h-[52px] rounded-full px-8 bg-obsidian text-white hover:bg-obsidian/90 text-base font-bold transition-colors duration-150"
            >
              Get started
            </Button>
          </Link>
          <Link to="/cxlogin">
            <Button
              data-testid="cta-login-btn"
              variant="outline"
              className="h-13 min-h-[52px] rounded-full px-8 border-white/60 text-white bg-transparent hover:bg-white/10 text-base font-semibold transition-colors duration-150"
            >
              Log In
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-obsidian text-white">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 grid sm:grid-cols-2 lg:grid-cols-5 gap-10">
        <div className="sm:col-span-2 lg:col-span-2 max-w-sm">
          <img src="/logo-wordmark.png" alt="The Tiffin by The Virsa" className="h-10 object-contain brightness-0 invert" />
          <p className="mt-4 text-white/60 leading-relaxed">
            Daily Fresh. Daily Different. Home-style Indian tiffins on a simple prepaid subscription.
          </p>
          <div className="mt-6 flex gap-3">
            <a href={BUSINESS.instagram} data-testid="footer-instagram" aria-label="Instagram" className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-saffron transition-colors duration-150">
              <Instagram size={18} />
            </a>
            <a href={BUSINESS.facebook} data-testid="footer-facebook" aria-label="Facebook" className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-saffron transition-colors duration-150">
              <Facebook size={18} />
            </a>
          </div>
        </div>

        <div>
          <p className="font-heading font-700 text-sm uppercase tracking-wide text-white/50">Explore</p>
          <ul className="mt-4 space-y-3">
            {NAV_LINKS.map((l) => (
              <li key={l.href}>
                <a href={l.href} className="text-white/80 hover:text-saffron transition-colors duration-150">
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="font-heading font-700 text-sm uppercase tracking-wide text-white/50">Account</p>
          <ul className="mt-4 space-y-3">
            <li>
              <Link to="/cxlogin" data-testid="footer-login-link" className="text-white/80 hover:text-saffron transition-colors duration-150">
                Customer Log In
              </Link>
            </li>
            <li>
              <Link to="/register" data-testid="footer-register-link" className="text-white/80 hover:text-saffron transition-colors duration-150">
                Create Account
              </Link>
            </li>
            <li>
              <Link to="/staff/login" data-testid="footer-staff-login-link" className="text-white/50 hover:text-white/80 transition-colors duration-150 text-sm">
                Staff &amp; Admin Login
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <p className="font-heading font-700 text-sm uppercase tracking-wide text-white/50">Legal</p>
          <ul className="mt-4 space-y-3">
            {LEGAL_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-legal-${l.to.slice(1)}`}
                  className="text-white/80 hover:text-saffron transition-colors duration-150"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-white/50">
          <p>© {new Date().getFullYear()} The Tiffin by The Virsa. All rights reserved.</p>
          <p>{BUSINESS.city}</p>
        </div>
      </div>
    </footer>
  );
}

export default function Marketing() {
  return (
    <div className="min-h-screen bg-bone">
      <InstallBanner />
      <Header />
      <main>
        <Hero />
        <Highlights />
        <HowItWorks />
        <Plans />
        <Food />
        <AppPromo />
        <Visit />
        <CtaBanner />
      </main>
      <Footer />
    </div>
  );
}
